/**
 * @module  js/services/crmconnectionservice.js
 * @class   CRMConnectionService
 * @summary Manages connected CRM provider and initiates HubSpot or Salesforce OAuth flows.
 *
 * @description
 * Detects, caches, and updates the currently linked CRM for the logged-in user.
 * Persists provider choice in localStorage and performs authenticated fetches to backend status and OAuth endpoints.
 *
 * ## Key Features
 * – Caches provider in localStorage to avoid redundant backend calls
 * – Generates provider-specific OAuth URLs and redirects the browser
 * – Simple helper to disconnect and clear cached state
 *
 * ## Public API
 * – `isConnected()` — returns a boolean indicating if any CRM is linked
 * – `getConnectedProvider()` — resolves "hubspot" | "salesforce" | "local" | null with backend validation
 * – `connectHubSpot()` — kicks off HubSpot OAuth installation flow
 * – `connectSalesforce()` — kicks off Salesforce OAuth installation flow
 * – `disconnect()` — severs the current CRM link and clears cache
 *
 * ## Dependencies
 * { AuthService, EndpointService }
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-04-28
 */

import { getCurrentCRM } from './crmUtils.js';

export default class CRMConnectionService {
  /** @type {import('../core/endpoints-service.js').EndpointService} */
  #endpoints;
  /** @type {import('./authservice.js').default} */
  #auth;
  #api;

  /** Local-storage cache key */
  static #LS_KEY = 'topsail.connectedCRM'; // hubspot | salesforce | local
  static #OAUTH_PENDING_KEY = 'topsail.crm.oauth.pending';

  /**
   * @param {{ endpointService: any, authService: any }} deps
   */
  constructor({ endpointService, authService, apiClient }) {
    if (!endpointService || !authService) {
      throw new Error('CRMConnectionService: missing dependencies');
    }
    this.#endpoints = endpointService;
    this.#auth = authService;
    this.#api = apiClient;
  }

  /* ───────────────────────── Public helpers ───────────────────────── */

  /** Convenience wrapper */
  async isConnected() {
    return Boolean(await this.getConnectedProvider());
  }

  /**
   * Returns `"hubspot" | "salesforce" | "local" | null`
   * – first checks localStorage, then calls backend `/crm-status`.
   */
  async getConnectedProvider(userId) {
    const cached = localStorage.getItem(CRMConnectionService.#LS_KEY);
    if (cached && !userId) return cached;

    try {
      const uid = userId ?? (await this.#auth.requireAuth()).uid;

      let provider = null;

      if (typeof this.#endpoints.crmStatus === 'function') {
        const url = this.#endpoints.crmStatus({ userId: uid });
        const res = await this.#fetchJson(url, { auth: true });
        provider = res.provider ?? null;
      } else {
        provider = await getCurrentCRM(uid, {
          authService: this.#auth,
          forceRefresh: Boolean(userId),
        });
      }

      if (!userId && provider) {
        this.#persist(provider);
      } else if (!userId) {
        localStorage.removeItem(CRMConnectionService.#LS_KEY);
      }

      return provider;
    } catch {
      return null; // network failure → treat as none
    }
  }

  /** Start HubSpot OAuth */
  connectHubSpot() {
    return this.#startOAuth('hubspot');
  }
  /** Start Salesforce OAuth */
  connectSalesforce() {
    return this.#startOAuth('salesforce');
  }

  /** Disconnect currently linked CRM */
  async disconnect() {
    const user = await this.#auth.requireAuth();
    const url = this.#endpoints.crmDisconnect({ userId: user.uid });

    await this.#fetchJson(url, { auth: true });
    localStorage.removeItem(CRMConnectionService.#LS_KEY);
  }

  /* ───────────────────────── Internals ────────────────────────────── */

  async #startOAuth(provider) {
    const user = await this.#auth.requireAuth();

    // Prefer the unified generateAuthUrl endpoint (supports Salesforce + HubSpot);
    // fall back to the legacy HubSpot-only install endpoint if needed.
    const params = {
      userId: user.uid,
      crm: provider,
      tenantId: user.tenantId,
      provider, // legacy param for generateInstallUrl
    };

    const buildUrl = () => {
      if (typeof this.#endpoints.generateAuthUrl === 'function') {
        return this.#endpoints.generateAuthUrl(params);
      }
      return this.#endpoints.generateInstallUrl(params);
    };

    const { installUrl, oauthUrl } = await this.#fetchJson(buildUrl(), {
      auth: true,
    });

    const redirectUrl = oauthUrl || installUrl;
    if (!redirectUrl) {
      throw new Error(`ApiClient is missing OAuth URL for ${provider}`);
    }

    try {
      sessionStorage.setItem(CRMConnectionService.#OAUTH_PENDING_KEY, provider);
    } catch {
      // best-effort only
    }

    window.location.href = redirectUrl; // hand control to the provider
  }

  #persist(provider) {
    localStorage.setItem(CRMConnectionService.#LS_KEY, provider);
  }

  /**
   * Internal helper – runs an authenticated JSON request via ApiClient.
   * @param {string} url                       Fully-qualified backend URL
   * @param {{ auth?: boolean, body?: any, method?: string }} [opts]
   *        auth   – add Firebase ID-token as Bearer if true
   *        body   – plain JS object (will be JSON-stringified)
   *        method – overrides default "POST" when a body is supplied
   * @returns {Promise<any>}                   Parsed JSON response
   */
  async #fetchJson(url, { auth = false, body, method } = {}) {
    return this.#api.fetchJson(url, {
      auth,
      method, // ApiClient defaults to POST when body is present
      body,
    });
  }
}
